import React, { useState, useEffect } from 'react';
import { X, User, Mail, Key, UserCog, AlertCircle, Loader2 } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { debugLog } from '../../utils/logger';

const UserForm = ({ user = null, onClose, onSuccess }) => {
    const { getAuthenticatedApi, isAdmin } = useAuth();
    const api = getAuthenticatedApi();

    const [formData, setFormData] = useState({
        username: '',
        email: '',
        password: '',
        name: '',
        role_id: '',
        active: true
    });

    const [roles, setRoles] = useState([]);
    
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const [formErrors, setFormErrors] = useState({});

    useEffect(() => {
        fetchRoles();
        
        if (user) {
            setFormData({
                username: user.username,
                email: user.email,
                password: '', // Non mostriamo la password attuale
                name: user.name || '',
                role_id: user.role_id,
                active: user.active
            });
        }
        
        setLoading(false);
    }, [user]);

    const fetchRoles = async () => {
        try {
            const response = await api.get('/user_role.php');
            // Filtra solo i ruoli admin e standalone
            const filteredRoles = response.data.filter(role => 
                role.name === 'admin' || role.name === 'standalone'
            );
            setRoles(filteredRoles);
        } catch (err) {
            console.error('Error fetching roles:', err);
            setError('Errore nel caricamento dei ruoli');
        }
    };

    const validateForm = () => {
        const errors = {};

        if (!formData.username.trim()) errors.username = 'Username obbligatorio';
        if (!user && !formData.password) errors.password = 'Password obbligatoria';
        if (!formData.email) errors.email = 'Email obbligatoria';
        if (!formData.role_id) errors.role_id = 'Ruolo obbligatorio';

        // Validazione email
        if (formData.email && !/\S+@\S+\.\S+/.test(formData.email)) {
            errors.email = 'Email non valida';
        }

        // Validazione password (solo per nuovi utenti o se si cambia la password)
        if (formData.password && formData.password.length < 8) {
            errors.password = 'La password deve essere di almeno 8 caratteri';
        }

        setFormErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!validateForm()) return;
        
        try {
            setSaving(true);
            setError(null);
            
            const dataToSend = {
                ...formData,
                role_id: parseInt(formData.role_id, 10),
                active: formData.active ? 1 : 0
            };
            
            const response = user 
                ? await api.put(`/users.php?id=${user.id}`, dataToSend)
                : await api.post('/users.php', dataToSend);
            
            onSuccess(response.data.user);
            onClose();
        } catch (err) {
            console.error('Errore durante il salvataggio:', err);
            
            const errorMessage = err.response?.data?.error || 
                                'Errore durante il salvataggio dell\'utente';
            
            setError(errorMessage);
        } finally {
            setSaving(false);
        }
    };

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));

        if (formErrors[name]) {
            setFormErrors(prev => ({ ...prev, [name]: '' }));
        }
    };

    if (loading) {
        return (
            <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
                <div className="bg-white dark:bg-gray-800 rounded-lg p-6">
                    <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
                </div>
            </div>
        );
    }

    return (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-2xl">
                <div className="flex justify-between items-center mb-6">
                    <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                        {user ? 'Modifica Utente' : 'Nuovo Utente'}
                    </h2>
                    <button
                        onClick={onClose}
                        className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                    >
                        <X className="h-6 w-6" />
                    </button>
                </div>

                {error && (
                    <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg text-red-700 dark:text-red-300 flex items-center">
                        <AlertCircle className="h-5 w-5 mr-2 flex-shrink-0" />
                        {error}
                    </div>
                )}

                <form onSubmit={handleSubmit} className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                            <label className="block text-gray-700 dark:text-gray-300 mb-2" htmlFor="username">
                                Username*
                            </label>
                            <div className="relative">
                                <User className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
                                <input
                                    type="text"
                                    id="username"
                                    name="username"
                                    value={formData.username}
                                    onChange={handleInputChange}
                                    className={`w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                                        formErrors.username 
                                            ? 'border-red-500 dark:border-red-400' 
                                            : 'border-gray-300 dark:border-gray-600'
                                    } bg-white dark:bg-gray-700 text-gray-900 dark:text-white`}
                                    placeholder="Inserisci username"
                                />
                            </div>
                            {formErrors.username && (
                                <p className="mt-1 text-sm text-red-600 dark:text-red-400">{formErrors.username}</p>
                            )}
                        </div>

                        <div>
                            <label className="block text-gray-700 dark:text-gray-300 mb-2" htmlFor="email">
                                Email*
                            </label>
                            <div className="relative">
                                <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
                                <input
                                    type="email"
                                    id="email"
                                    name="email"
                                    value={formData.email}
                                    onChange={handleInputChange}
                                    className={`w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                                        formErrors.email 
                                            ? 'border-red-500 dark:border-red-400' 
                                            : 'border-gray-300 dark:border-gray-600'
                                    } bg-white dark:bg-gray-700 text-gray-900 dark:text-white`}
                                    placeholder="Inserisci email"
                                />
                            </div>
                            {formErrors.email && (
                                <p className="mt-1 text-sm text-red-600 dark:text-red-400">{formErrors.email}</p>
                            )}
                        </div>

                        <div>
                            <label className="block text-gray-700 dark:text-gray-300 mb-2" htmlFor="name">
                                Nome Completo
                            </label>
                            <input
                                type="text"
                                id="name"
                                name="name"
                                value={formData.name}
                                onChange={handleInputChange}
                                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                placeholder="Inserisci nome completo"
                            />
                        </div>

                        <div>
                            <label className="block text-gray-700 dark:text-gray-300 mb-2" htmlFor="password">
                                Password{!user && '*'}
                            </label>
                            <div className="relative">
                                <Key className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
                                <input
                                    type="password"
                                    id="password"
                                    name="password"
                                    value={formData.password}
                                    onChange={handleInputChange}
                                    className={`w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                                        formErrors.password 
                                            ? 'border-red-500 dark:border-red-400' 
                                            : 'border-gray-300 dark:border-gray-600'
                                    } bg-white dark:bg-gray-700 text-gray-900 dark:text-white`}
                                    placeholder={user ? "Lascia vuoto per non modificare" : "Inserisci password"}
                                />
                            </div>
                            {formErrors.password && (
                                <p className="mt-1 text-sm text-red-600 dark:text-red-400">{formErrors.password}</p>
                            )}
                        </div>

                        <div>
                            <label className="block text-gray-700 dark:text-gray-300 mb-2" htmlFor="role_id">
                                Ruolo*
                            </label>
                            <div className="relative">
                                <UserCog className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
                                <select
                                    id="role_id"
                                    name="role_id"
                                    value={formData.role_id}
                                    onChange={handleInputChange}
                                    className={`w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                                        formErrors.role_id 
                                            ? 'border-red-500 dark:border-red-400' 
                                            : 'border-gray-300 dark:border-gray-600'
                                    } bg-white dark:bg-gray-700 text-gray-900 dark:text-white`}
                                >
                                    <option value="">Seleziona ruolo</option>
                                    {roles.map(role => (
                                        <option key={role.id} value={role.id}>
                                            {role.name === 'admin' ? 'Amministratore' : 'Utente Standalone'}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            {formErrors.role_id && (
                                <p className="mt-1 text-sm text-red-600 dark:text-red-400">{formErrors.role_id}</p>
                            )}
                        </div>

                        <div className="flex items-center">
                            <input
                                type="checkbox"
                                id="active"
                                name="active"
                                checked={formData.active}
                                onChange={handleInputChange}
                                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                            />
                            <label className="ml-2 block text-gray-700 dark:text-gray-300" htmlFor="active">
                                Utente attivo
                            </label>
                        </div>
                    </div>

                    <div className="flex justify-end space-x-4">
                        <button
                            type="button"
                            onClick={onClose}
                            className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-200 dark:bg-gray-700 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
                        >
                            Annulla
                        </button>
                        <button
                            type="submit"
                            disabled={saving}
                            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center"
                        >
                            {saving && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
                            {user ? 'Aggiorna' : 'Crea'} Utente
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default UserForm;